import { ArrowBack } from '@mui/icons-material';
import { AppBar, Box, Link, Toolbar, Typography } from '@mui/material';
import { process } from '@progress/kendo-data-query';
import { Grid, GridColumn } from '@progress/kendo-react-grid';
import '@progress/kendo-theme-default/dist/all.css';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect } from 'react-router';

import { AsyncContent } from '~/components/AsyncContent';
import { ColumnMenu, ColumnProps } from '~/components/Kendo/ColumnMenu';
import { Fields } from '~/components/Kendo/ColumnMenu/useColumnMenu';

import {
  AvatarCell,
  CompensationCell,
  DateCell,
  IdCell,
  MoreMenuCell,
  PerformanceCell,
  PositionCell,
  SalaryCell,
} from './components';
import { useMyTeam } from './useMyTeam';
import { useSxStyles } from './useStyles';

export const MyTeam: React.FC = () => {
  const { t } = useTranslation();
  const sxStyles = useSxStyles();
  const {
    loading,
    failedToLoad,
    employeesDataTable,
    gridDataState,
    handleGridDataStateChange,
    handleGridPageChange,
  } = useMyTeam();

  const gridColumns: Array<ColumnProps> = [
    {
      cell: AvatarCell,
      field: 'firstName',
      filter: 'text',
      groupable: false,
      title: t('employee'),
      show: true,
    },
    {
      cell: IdCell,
      field: 'id',
      title: t('empId'),
      groupable: false,
      filter: 'text',
      show: true,
    },
    {
      cell: PositionCell,
      field: 'currentPosition',
      filter: 'text',
      groupable: true,
      title: t('position'),
      show: true,
    },
    {
      cell: SalaryCell,
      field: 'currentSalary',
      filter: 'numeric',
      groupable: true,
      title: t('baseSalary'),
      show: true,
    },
    {
      cell: CompensationCell,
      field: 'currentCompensation',
      filter: 'numeric',
      groupable: true,
      title: t('compensation'),
      show: true,
    },
    {
      cell: DateCell,
      field: 'originalStartDate',
      title: t('startOfEmp'),
      groupable: true,
      filter: 'date',
      format: '{L}',
      show: true,
    },
    {
      cell: PerformanceCell,
      field: 'currentPerformance',
      title: t('performance'),
      groupable: true,
      filter: 'numeric',
      show: true,
    },
    {
      cell: MoreMenuCell,
      field: 'menuButton',
      title: ' ',
      groupable: false,
      width: '100px',
      sortable: false,
      show: true,
    },
  ];
  const [showMyWorkspace, setShowMyWorkspace] = React.useState(false);
  const [columns, setColumns] = React.useState(gridColumns);

  if (showMyWorkspace) {
    return <Redirect to="/my-workspace" />;
  }

  return (
    <Box sx={sxStyles.root}>
      <AppBar position="relative">
        <Toolbar>
          <Link onClick={() => setShowMyWorkspace(true)}>
            <ArrowBack sx={sxStyles.icon} />
          </Link>
          <Typography sx={sxStyles.text} variant="h6">
            {t('myWorkspace')}
          </Typography>
        </Toolbar>
      </AppBar>
      <AsyncContent
        noData={!employeesDataTable.length}
        noDataMessage={t('noEmployees')}
        isLoading={loading}
        failedToLoad={failedToLoad}
      >
        <Grid
          data={process(employeesDataTable, gridDataState)}
          pageable={true}
          resizable={true}
          sortable={true}
          groupable={true}
          {...gridDataState}
          onDataStateChange={handleGridDataStateChange}
          onPageChange={handleGridPageChange}
          total={employeesDataTable.length}
          style={sxStyles.grid}
        >
          {columns.map(
            (column, idx) =>
              column.show && (
                <GridColumn
                  {...column}
                  key={idx}
                  columnMenu={
                    column.field === Fields.MENUBUTTON
                      ? undefined
                      : (otherProps) => (
                          <ColumnMenu
                            {...otherProps}
                            columns={columns.filter(
                              (col) =>
                                ![Fields.MENUBUTTON.toString()].includes(
                                  col.field || '',
                                ),
                            )}
                            onColumnsSubmit={setColumns}
                          />
                        )
                  }
                />
              ),
          )}
        </Grid>
      </AsyncContent>
    </Box>
  );
};
